/**
 * Opens a roadmap view already scrolled to the right place: where the
 * student left it earlier in this tab, or else on their next item.
 */
import {useRoadmapStyle} from 'contexts/RoadmapStyleContext';
import {useLayoutEffect, useState, type RefObject} from 'react';

/** How far down the visible page the start item's centre sits. */
const START_ITEM_FRACTION = 1 / 3;

/** Input that means the student has taken over the scroll position. */
const TAKEOVER_EVENTS = ['wheel', 'touchstart', 'pointerdown', 'keydown'];

/**
 * The nearest ancestor that actually scrolls vertically. The overflow check
 * alone is not enough: a computed `overflow-y` of `auto` also results from
 * `overflow-x: hidden`, on boxes that grow with their content.
 *
 * @param element - Where to start looking, exclusive.
 * @return The scroller, or null when no ancestor scrolls.
 */
function scrollerAbove(element: HTMLElement): HTMLElement | null {
  for (let node = element.parentElement; node; node = node.parentElement) {
    const {overflowY} = getComputedStyle(node);
    const canScroll = overflowY === 'auto' || overflowY === 'scroll';
    if (canScroll && node.scrollHeight > node.clientHeight) {
      return node;
    }
  }
  return null;
}

/**
 * Sets the page's scroll position for a roadmap view before it is first
 * painted, then keeps the view still until the student touches the page.
 *
 * The position is the one the student scrolled to the last time this view
 * was open in this tab, kept in `RoadmapStyleContext`'s `scrollOffsets`.
 * When they never scrolled it, the view opens with the start item's centre
 * a third of the way down the visible page, or at the top when the item
 * already sits that high there.
 *
 * Only the view's nearest scrolling ancestor is moved, by setting its
 * `scrollTop`. Until the student scrolls, clicks, touches or types, content
 * that changes size above the view (a calendar that finishes loading) is
 * compensated in the same frame, before paint, so nothing is seen to move.
 * Chrome does this natively; Safari does not.
 *
 * @param args.viewRef - The element wrapping the view.
 * @param args.memoryKey - Identifies the view's remembered position; give
 *   each user, course and view its own.
 * @param args.startItemId - The item to open on, matched against the
 *   `data-item-id` attributes the maps render; the first button inside the
 *   match is measured. Only its value on first render is used. When
 *   omitted, or when no element matches, the view opens at the top.
 * @return Nothing.
 * @throws Never.
 */
export function useRoadmapStartPosition({
  viewRef,
  memoryKey,
  startItemId,
}: {
  viewRef: RefObject<HTMLElement | null>;
  memoryKey: string;
  startItemId?: string;
}): void {
  const {scrollOffsets} = useRoadmapStyle();
  const [initialStartItemId] = useState(startItemId);

  useLayoutEffect(() => {
    const view = viewRef.current;
    const scroller = view ? scrollerAbove(view) : null;
    if (!view || !scroller) {
      return;
    }
    const offsetOf = (element: Element) =>
      element.getBoundingClientRect().top -
      scroller.getBoundingClientRect().top +
      scroller.scrollTop;
    const startItem =
      initialStartItemId === undefined
        ? null
        : view.querySelector(
            `[data-item-id="${CSS.escape(initialStartItemId)}"] button`,
          );
    const startPosition = (): number => {
      const remembered = scrollOffsets.get(memoryKey);
      if (remembered !== undefined) {
        return offsetOf(view) + remembered;
      }
      if (!startItem) {
        return 0;
      }
      const {height} = startItem.getBoundingClientRect();
      return (
        offsetOf(startItem) +
        height / 2 -
        scroller.clientHeight * START_ITEM_FRACTION
      );
    };

    let isHolding = true;
    const hold = () => {
      if (isHolding) {
        scroller.scrollTop = startPosition();
      }
    };
    hold();

    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(hold);
    observer?.observe(scroller);
    observer?.observe(view);
    for (const child of Array.from(scroller.children)) {
      observer?.observe(child);
    }
    const release = () => {
      isHolding = false;
      observer?.disconnect();
    };
    const remember = () => {
      // Scrolls fired while the page is torn down on navigation, or by the
      // hold itself, are not the student's.
      if (isHolding || !view.isConnected) {
        return;
      }
      scrollOffsets.set(memoryKey, scroller.scrollTop - offsetOf(view));
    };
    for (const type of TAKEOVER_EVENTS) {
      window.addEventListener(type, release, {capture: true, passive: true});
    }
    scroller.addEventListener('scroll', remember, {passive: true});
    return () => {
      observer?.disconnect();
      for (const type of TAKEOVER_EVENTS) {
        window.removeEventListener(type, release, {capture: true});
      }
      scroller.removeEventListener('scroll', remember);
    };
  }, [viewRef, memoryKey, scrollOffsets, initialStartItemId]);
}
